/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { TabContainer, Nav, Tab, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { pageToOffset } from 'utils/pageToOffset';
import customerEntityService from 'service/charterer';
import PageBreadcrumb, {
  PageBreadcrumbItem
} from 'components/common/PageBreadcrumb';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import Button from 'components/base/Button';
import ButtonRefresh from 'components/base/ButtonRefresh';

export type TableDataTypes = {
  customer: string;
  currency: string;
  phone: string;
  amounts: string;
  status: string;
  guid: string;
  currency_pid: number;
  next_states: any;
  gupid: number;
  state: number;
  status_pid: number;
};

const ChartererProposal = () => {
  const { t, i18n } = useTranslation();

  const cashboxId = useSelector((state: any) => state?.shiftId?.shiftId);
  const cashboxPid = useSelector((state: any) => state?.shiftId?.shiftPid);

  const [key, setKey] = useState('active');
  const [activePageCount, setActivePageCount] = useState(1);
  const [activeCurrentPage, setActiveCurrentPage] = useState(1);
  const [notActivePageCount, setNotActivePageCount] = useState(1);
  const [notActiveCurrentPage, setNotActiveCurrentPage] = useState(1);

  const {
    data: dataActive,
    isLoading: isLoadingActive,
    isFetching: isFetchingActive,
    refetch: refetchActive
  } = useQuery(
    ['GET_CHARTERER_OUT', activeCurrentPage],
    () => {
      const res = customerEntityService
        .getListProposalChartererOut({
          offset: pageToOffset(activeCurrentPage, 10),
          limit: 10
        })
        .then((res: any) => res);
      return res;
    },
    {
      enabled: !!activeCurrentPage
    }
  );

  useEffect(() => {
    if (dataActive) {
      setActivePageCount(Math.ceil(dataActive?.count / 10));
    }
  }, [dataActive]);

  const loadingActive = isLoadingActive || isFetchingActive;

  const {
    data: dataNotActive,
    isLoading: isLoadingNotActive,
    isFetching: isFetchingNotActive,
    refetch: refetchNotActive
  } = useQuery(
    ['GET_CHARTERER_IN', notActiveCurrentPage],
    () => {
      const res = customerEntityService
        .getListProposalChartererIn({
          offset: pageToOffset(notActiveCurrentPage, 10),
          limit: 10
        })
        .then((res: any) => res);
      return res;
    },
    {
      enabled: !!notActiveCurrentPage
    }
  );

  useEffect(() => {
    if (dataNotActive) {
      setNotActivePageCount(Math.ceil(dataNotActive?.count / 10));
    }
  }, [dataNotActive]);

  const loadingNotActive = isLoadingNotActive || isFetchingNotActive;

  const chartererOutPropsalData: TableDataTypes[] = useMemo(() => {
    return (
      dataActive?.items?.map((items: any) => {
        return {
          customer: items?.legal_entity?.name,
          phone: items?.legal_entity?.phone_number,
          amount: items?.amount
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.amount)
                .replace(/,/g, ' ')
            : 0,
          status: items?.state_info?.name?.[i18n?.language],
          provider_indentification_number:
            items?.provider_indentification_number || '',
          provider_name: items?.provider_name || '',
          payment_type: items?.payment_type?.name?.[i18n?.language] || '',
          status_pid: items?.state_info?.pid || 0,
          currency: items?.currency?.name?.[i18n?.language],
          next_states: items?.next_states || [],
          gupid: items?.pid
        };
      }) ?? []
    );
  }, [dataActive, t, i18n?.language]);

  const chartererInPropsalData: TableDataTypes[] = useMemo(() => {
    return (
      dataNotActive?.items?.map((items: any) => {
        return {
          customer: items?.legal_entity?.name,
          phone: items?.legal_entity?.phone_number,
          amount: items?.amount
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.amount)
                .replace(/,/g, ' ')
            : 0,
          status: items?.state_info?.name?.[i18n?.language],
          provider_indentification_number:
            items?.provider_indentification_number || '',
          provider_name: items?.provider_name || '',
          payment_type: items?.payment_type?.name?.[i18n?.language] || '',
          status_pid: items?.state_info?.pid || 0,
          state: items?.state || 0,
          currency: items?.currency?.name?.[i18n?.language],
          next_states: items?.next_states || [],
          gupid: items?.pid
        };
      }) ?? []
    );
  }, [dataNotActive, t, i18n?.language]);

  const buyyersBreadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: 'dt_kt',
      url: '/dtkt/provider'
    },
    {
      label: 'customer',
      url: '/dtkt/customer'
    },
    {
      label: 'applications',
      active: true
    }
  ];

  const chartererOutProposalDataTableColumns: ColumnDef<TableDataTypes>[] = [
    {
      accessorKey: 'order_number',
      enableSorting: false,
      header: '№',
      cell: rowData => {
        const orderNumber =
          rowData.row.index + 1 + (activeCurrentPage - 1) * 10;
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'customer',
      header: t('customer'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'phone',
      header: t('phone'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'amount',
      header: t('amounts'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'payment_type',
      header: t('payment_procedure'),
      meta: {
        cellProps: { className: 'text-900 ' }
      }
    },

    {
      accessorKey: 'provider_name',
      header: t('firm_name'),
      meta: {
        cellProps: { className: 'text-900 ' }
      }
    },

    {
      accessorKey: 'provider_indentification_number',
      header: t('firm_pnfl'),
      meta: {
        cellProps: { className: 'text-900 ' }
      }
    },

    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status, status_pid } = rowData?.row?.original || {};
        const statusColors: Record<number, { bg: string; text: string }> = {
          1: { bg: '#e0f7fa', text: '#00796b' },
          2: { bg: '#fffde7', text: '#fbc02d' },
          3: { bg: '#e0f7fa', text: '#00796b' },
          4: { bg: '#fffde7', text: '#fbc02d' },
          5: { bg: '#e8f5e9', text: '#2e7d32' },
          6: { bg: '#e8f5e9', text: '#2e7d32' },
          7: { bg: '#e8f5e9', text: '#2e7d32' },
          8: { bg: '#ffebee', text: '#d32f2f' },
          9: { bg: '#ffebee', text: '#d32f2f' }
        };

        return (
          <Button
            style={{
              backgroundColor: statusColors[status_pid]?.bg || '#eceff1',
              color: statusColors[status_pid]?.text || '#37474f',
              border: 'none',
              whiteSpace: 'nowrap'
            }}
          >
            {status}
          </Button>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const chartererInProposalDataTableColumns: ColumnDef<TableDataTypes>[] = [
    {
      accessorKey: 'order_number',
      enableSorting: false,
      header: '№',
      cell: rowData => {
        const orderNumber =
          rowData.row.index + 1 + (activeCurrentPage - 1) * 10;
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'customer',
      header: t('customer'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'phone',
      header: t('phone'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'amount',
      header: t('amounts'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'payment_type',
      header: t('payment_procedure'),
      meta: {
        cellProps: { className: 'text-900 ' }
      }
    },

    {
      accessorKey: 'provider_name',
      header: t('firm_name'),
      meta: {
        cellProps: { className: 'text-900 ' }
      }
    },

    {
      accessorKey: 'provider_indentification_number',
      header: t('firm_pnfl'),
      meta: {
        cellProps: { className: 'text-900 ' }
      }
    },

    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status, status_pid } = rowData?.row?.original || {};
        const statusColors: Record<number, { bg: string; text: string }> = {
          1: { bg: '#e0f7fa', text: '#00796b' },
          2: { bg: '#fffde7', text: '#fbc02d' },
          3: { bg: '#e0f7fa', text: '#00796b' },
          4: { bg: '#fffde7', text: '#fbc02d' },
          5: { bg: '#e8f5e9', text: '#2e7d32' },
          6: { bg: '#e8f5e9', text: '#2e7d32' },
          7: { bg: '#e8f5e9', text: '#2e7d32' },
          8: { bg: '#ffebee', text: '#d32f2f' },
          9: { bg: '#ffebee', text: '#d32f2f' }
        };

        return (
          <Button
            style={{
              backgroundColor: statusColors[status_pid]?.bg || '#eceff1',
              color: statusColors[status_pid]?.text || '#37474f',
              border: 'none',
              whiteSpace: 'nowrap'
            }}
          >
            {status}
          </Button>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      id: 'action',
      cell: rowData => {
        const { next_states, gupid } = rowData.row.original;

        const [loading, setLoading] = useState({
          confirm: false,
          reject: false
        });

        const confirmState = next_states.find((state: any) =>
          [1, 2, 3, 5, 6, 7].includes(state.pid)
        );

        const rejectState = next_states.find((state: any) => state.pid === 8);

        const handleSubmit = async () => {
          setLoading(prev => ({ ...prev, confirm: true }));
          try {
            const res = await customerEntityService.updateProposalCreditor(
              gupid,
              {
                cashbox_id: cashboxId,
                cashbox_pid: cashboxPid,
                state: confirmState?.pid
              }
            );
            if (res) {
              refetchActive();
              refetchNotActive();
            }
          } catch (error) {
            console.error('Confirm error:', error);
          } finally {
            setLoading(prev => ({ ...prev, confirm: false }));
          }
        };

        const handleReject = async () => {
          setLoading(prev => ({ ...prev, reject: true }));
          try {
            const res = await customerEntityService.updateProposalCreditor(
              gupid,
              {
                cashbox_id: cashboxId,
                cashbox_pid: cashboxPid,
                state: rejectState?.pid
              }
            );
            if (res) {
              refetchActive();
              refetchNotActive();
            }
          } catch (error) {
            console.error('Reject error:', error);
          } finally {
            setLoading(prev => ({ ...prev, reject: false }));
          }
        };

        return (
          <>
            {next_states?.length > 0 && (
              <div className="d-flex justify-content-center gap-2">
                {confirmState && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit}
                    disabled={loading.confirm}
                  >
                    {loading.confirm ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      t('confirm')
                    )}
                  </Button>
                )}

                {rejectState && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleReject}
                    disabled={loading.reject}
                  >
                    {loading.reject ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      t('reject')
                    )}
                  </Button>
                )}
              </div>
            )}
          </>
        );
      },

      meta: {
        headerProps: { style: { width: '15%' } },
        cellProps: { className: 'text-center' }
      }
    }
  ];

  const chartererOutTable = useAdvanceTable({
    data: chartererOutPropsalData,
    columns: chartererOutProposalDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const chartererInTable = useAdvanceTable({
    data: chartererInPropsalData,
    columns: chartererInProposalDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <div>
      <PageBreadcrumb items={buyyersBreadcrumbItems} />

      <div className="mb-5 d-flex gap-2 align-items-center">
        <h2>{t('applications')}</h2>
        <ButtonRefresh
          onRefresh={() => {
            if (key === 'active') {
              refetchActive();
            } else {
              refetchNotActive();
            }
          }}
        />
      </div>
      <TabContainer
        activeKey={key}
        onSelect={(k: string | null) => setKey(k ?? 'active')}
      >
        <Nav variant="underline" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="active">{t('outgoing')}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="not-active">{t('incoming')}</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="active">
            <AdvanceTableProvider {...chartererOutTable}>
              <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
                <AdvanceTable
                  tableProps={{ className: 'phoenix-table fs-9' }}
                  isLoading={loadingActive}
                />
                <AdvanceTableCPagination
                  count={activePageCount}
                  page={activeCurrentPage}
                  setCurrentPage={setActiveCurrentPage}
                />
              </div>
            </AdvanceTableProvider>
          </Tab.Pane>

          <Tab.Pane eventKey="not-active">
            <AdvanceTableProvider {...chartererInTable}>
              <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
                <AdvanceTable
                  tableProps={{ className: 'phoenix-table fs-9' }}
                  isLoading={loadingNotActive}
                />
                <AdvanceTableCPagination
                  count={notActivePageCount}
                  page={notActiveCurrentPage}
                  setCurrentPage={setNotActiveCurrentPage}
                />
              </div>
            </AdvanceTableProvider>
          </Tab.Pane>
        </Tab.Content>
      </TabContainer>
    </div>
  );
};

export default ChartererProposal;
